import { useState, useRef, useEffect } from 'react';
import './AutocompleteInput.css';

/**
 * Sugerencia: el texto que se muestra y, opcionalmente, el dato completo del
 * que salió. `data` permite que quien lo use haga algo más al elegirla —por
 * ejemplo, autorrellenar la dirección asociada a un proveedor.
 */
export interface Suggestion<T = unknown> {
  label: string;
  data?: T;
}

interface Props<T = unknown> {
  value: string;
  onChange: (value: string) => void;
  /**
   * Busca sugerencias para el texto tecleado. Se llama con debounce y su
   * resultado se descarta si llega tarde (petición obsoleta).
   */
  fetchSuggestions: (query: string) => Promise<Suggestion<T>[]>;
  /** Se dispara al ELEGIR una sugerencia (no al teclear a mano). */
  onPick?: (suggestion: Suggestion<T>) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Nº mínimo de caracteres antes de pedir sugerencias. */
  minChars?: number;
  /** Milisegundos de espera desde la última tecla. */
  debounceMs?: number;
  id?: string;
}

/**
 * Input de TEXTO LIBRE con sugerencias.
 *
 * A diferencia de un selector, aquí el usuario manda: puede escribir algo que no
 * está en la lista y se guarda tal cual. Las sugerencias solo ayudan a reutilizar
 * lo ya escrito antes y a no crear variantes del mismo nombre.
 *
 * Genérico a propósito (recibe `fetchSuggestions`): sirve para proveedor y para
 * cualquier campo futuro que deba "aprender" de lo tecleado.
 *
 * Degrada bien sin conexión: si la búsqueda falla, se comporta como un input normal.
 */
export function AutocompleteInput<T = unknown>({
  value, onChange, fetchSuggestions, onPick, placeholder, disabled,
  minChars = 2, debounceMs = 250, id,
}: Props<T>) {
  const [suggestions, setSuggestions] = useState<Suggestion<T>[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  // Contador de peticiones: si el usuario sigue tecleando, una respuesta
  // antigua no debe pisar a una más reciente.
  const requestIdRef = useRef(0);
  // Evita pedir sugerencias por el valor que acabamos de seleccionar.
  const skipNextRef = useRef(false);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (skipNextRef.current) { skipNextRef.current = false; return; }
    const query = value.trim();
    if (query.length < minChars) {
      setSuggestions([]);
      return;
    }
    const myId = ++requestIdRef.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const found = await fetchSuggestions(query);
        if (myId !== requestIdRef.current) return; // respuesta obsoleta
        setSuggestions(found);
        setOpen(found.length > 0);
      } catch {
        // Sin red: seguimos como input de texto normal, sin molestar.
        if (myId === requestIdRef.current) setSuggestions([]);
      } finally {
        if (myId === requestIdRef.current) setLoading(false);
      }
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [value, minChars, debounceMs, fetchSuggestions]);

  const pick = (s: Suggestion<T>) => {
    skipNextRef.current = true;
    onChange(s.label);
    onPick?.(s);
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div className="autocomplete-input" ref={ref}>
      <input
        id={id}
        className="cp-input"
        type="text"
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
      />
      {loading && <span className="autocomplete-input__spinner" aria-hidden="true" />}
      {open && !disabled && suggestions.length > 0 && (
        <ul className="autocomplete-input__list">
          {suggestions.map((s) => (
            <li
              key={s.label}
              className="autocomplete-input__opt"
              // onMouseDown + preventDefault: seleccionar antes de que el blur cierre la lista.
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
            >
              {s.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
